import { call, put, select, takeLatest } from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '@common-modules/snackbar';

import { selectConfigEngines } from '@common-modules/engines/selectors';
import {
  OCR_NOT_RUN,
  OCR_PROCESSING,
  PII_DETECTION_FAILED,
  PII_DETECTION_PROCESSING,
  PII_TIMED_OUT,
  PII_NOT_RUN,
  PII_RESULTS_LOADED,
  RUN_PII_DETECTION,
  RUN_PII_DETECTION_FAILURE,
  RUN_PII_DETECTION_SUCCESS,
} from '../actions';
import { selectSession } from '../selectors';
import {
  EngineTaskStatus,
  serviceCreateDocumentCognitionJob,
  serviceCreatePiiDetectionJob,
  serviceDownloadPiiResult,
  serviceFindPiiResultAssetUrl,
} from '../services';
import { parsePiiEngineResults } from '../helpers';
import { fetchPdfPageDimensions } from '../pdfPageDimensions';
import { TDOId } from '@common-modules/universal/models/Brands';
import { selectToken, selectGraphQLConfig } from './auth';

/**
 * What a status snapshot means for PII detection. `awaiting-task` matters: a
 * run whose task has not been found YET must not read as `settled`, or
 * the caller reports "finished but produced no results" on a healthy run.
 */
export type PiiDisposition =
  | 'running'
  | 'settled'
  | 'awaiting-task'
  /** Terminal, but not `complete`. */
  | 'failed'
  /** Created 12h ago and still not finished. */
  | 'stalled';

export function classifyPiiStatus({
  hasRunning,
  hasFailed,
  hasStalled,
  mayStillAppear,
}: EngineTaskStatus): PiiDisposition {
  if (hasStalled) {
    return 'stalled';
  }
  if (hasFailed) {
    return 'failed';
  }
  if (hasRunning) {
    return 'running';
  }
  if (mayStillAppear) {
    return 'awaiting-task';
  }
  return 'settled';
}

/** ~2 min. Mirrors the OCR guard; see MAX_POLLS_AWAITING_OCR_TASK. */
const MAX_POLLS_AWAITING_PII_TASK = 12;

/**
 * Find the engine's result asset, download + parse it, and dispatch
 * PII_RESULTS_LOADED. Returns true if results were loaded, false if no asset
 * exists yet. Uses a rotation-aware `getViewport` metadata pass to convert the
 * engine's normalized coordinates into the editor's PDF-point geometry.
 *
 * NOTE: this deliberately does NOT reuse the open-time pre-pass's
 * `session.pageDimensions` — those are `page.view` (raw MediaBox) extents, which
 * diverge from `getViewport` on `/Rotate`d or non-zero-origin pages and would
 * misplace PII boxes there. De-duplicating this parse is deferred to the phase-3
 * MuPDF work, which can supply rotation-correct dims (VE-27250).
 */
export function* loadPiiResults(
  tdoId: TDOId,
  piiEngineId: string,
  pdfUrl: string
) {
  const token = yield* select(selectToken);
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    yield* select(selectGraphQLConfig);

  const assetUrl = yield* call(
    serviceFindPiiResultAssetUrl,
    tdoId,
    piiEngineId,
    token,
    apiRoot,
    graphQLEndpoint,
    veritoneAppId
  );
  if (!assetUrl) {
    return false;
  }

  const result = yield* call(serviceDownloadPiiResult, assetUrl);
  const pageDimensions = yield* call(fetchPdfPageDimensions, pdfUrl);
  const detections = parsePiiEngineResults(result, pageDimensions);
  // Scoped: these are findings about THIS document's pages, and folding them
  // into whichever session is on screen would present them as findings about
  // the page in front of the reviewer.
  yield* put(PII_RESULTS_LOADED({ tdoId, detections }));
  return true;
}

/** Carried between ticks by the shared poller (`sagas/engineStatus.ts`). */
export interface PiiWatchState {
  /** Still interested in further snapshots. */
  active: boolean;
  /** Ticks spent waiting for a task that has never appeared. */
  pollsAwaitingTask: number;
}

export const initialPiiWatchState = (): PiiWatchState => ({
  active: true,
  pollsAwaitingTask: 0,
});

/**
 * Advance `piiEngineState` on one status reading; returns the next watch state.
 *
 * `firstReading` decides what "settled, no result asset" means: on open,
 * detection was never run (offer it); after a run, it produced nothing (fail).
 */
export function* stepPiiWatch(
  state: PiiWatchState,
  status: EngineTaskStatus,
  {
    tdoId,
    engineId,
    pdfUrl,
    firstReading,
  }: {
    tdoId: TDOId;
    engineId: string;
    pdfUrl: string;
    firstReading: boolean;
  }
) {
  const disposition = classifyPiiStatus(status);

  if (disposition === 'stalled' || disposition === 'failed') {
    // A run that broke or never finished is NOT "not run": the reviewer needs
    // to know it was attempted, or the Run button reads as never having been
    // pressed and the same failure is simply repeated.
    yield* put(
      disposition === 'stalled'
        ? PII_TIMED_OUT({ tdoId })
        : PII_DETECTION_FAILED({ tdoId })
    );
    return { ...state, active: false };
  }

  if (disposition === 'running') {
    if (firstReading) {
      yield* put(PII_DETECTION_PROCESSING({ tdoId }));
    }
    return state;
  }

  if (disposition === 'awaiting-task') {
    const waited = firstReading
      ? state.pollsAwaitingTask
      : state.pollsAwaitingTask + 1;
    if (waited < MAX_POLLS_AWAITING_PII_TASK) {
      // Live job, task not visible yet. NOT `not-run` — that would offer
      // "Run PII Detection" for a run already in flight.
      if (firstReading) {
        yield* put(PII_DETECTION_PROCESSING({ tdoId }));
      }
      return { ...state, pollsAwaitingTask: waited };
    }
    yield* put(PII_NOT_RUN({ tdoId }));
    return { ...state, active: false, pollsAwaitingTask: waited };
  }

  // Nothing in flight — results should be available.
  const loaded = yield* call(loadPiiResults, tdoId, engineId, pdfUrl);
  if (!loaded) {
    yield* put(
      firstReading
        ? PII_NOT_RUN({ tdoId })
        : RUN_PII_DETECTION_FAILURE({
            tdoId,
            message: 'PII detection finished but produced no results.',
          })
    );
  }
  return { ...state, active: false };
}

/**
 * The status read failed. Resolve `checking` so the spinner cannot stick.
 * Unlike OCR's `indeterminate` fallback: PII's un-run state is just an offer to
 * run it, always safe to show.
 *
 * `tdoId` comes from the watch that took the reading, not from whatever session
 * is on screen when it lands.
 */
export function* stepPiiWatchError(tdoId: TDOId, e: unknown) {
  console.warn(
    'Document open: PII status check failed; showing the un-run state.',
    e
  );
  yield* put(PII_NOT_RUN({ tdoId }));
}

/** No PII engine configured, or no PDF to run it against. */
export function* resolvePiiUnavailable(tdoId: TDOId) {
  yield* put(PII_NOT_RUN({ tdoId }));
}

/**
 * VE-21915 — "Run PII Detection" re-run (user-triggered).
 *
 * The reducer flips `piiEngineState` to `processing` optimistically on
 * RUN_PII_DETECTION. Here we create a job pointed directly at the document PII
 * detection engine (using the session's PDF signed URL), then poll until it
 * finishes and load the results. On submission failure we revert to `not-run`.
 */
export function* handleRunPiiDetection() {
  // Whether this click also started OCR, so a failure can revert BOTH engines.
  // Declared out here because the catch cannot see the try's bindings — as is
  // the document itself, since every revert has to name the document the run
  // was started on rather than whichever one is on screen when it fails.
  let startedOcr = false;
  let failedFor: TDOId | null = null;
  try {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    const { tdoId, pdfUrl } = session;
    failedFor = tdoId;
    if (!pdfUrl) {
      throw new Error('No PDF URL available for this document');
    }

    const {
      documentPIIDetectionEngineId,
      documentOcrEngineId,
      defaultClusterId,
    } = yield* select(selectConfigEngines);
    if (!documentPIIDetectionEngineId) {
      throw new Error('documentPIIDetectionEngineId is not configured');
    }

    const token = yield* select(selectToken);
    const { apiRoot, graphQLEndpoint, veritoneAppId } =
      yield* select(selectGraphQLConfig);

    /**
     * OCR FIRST WHERE IT HAS NOT RUN. Detection reads a text layer, so on a
     * scan that has never been through OCR a PII-only job would read nothing
     * and report a clean document — the worst possible answer. Chained in ONE
     * job so PII reads the searchable copy OCR republishes rather than the file
     * as it is now.
     */
    const needsOcrFirst =
      !!documentOcrEngineId && session.ocrEngineState === 'not-run';

    if (needsOcrFirst) {
      // Before the round trip, not after: `RUN_PII_DETECTION` already flipped
      // PII to `processing` on the click, and OCR lagging it by a request makes
      // the notice contradict the panel for a second or two.
      startedOcr = true;
      yield* put(OCR_PROCESSING({ tdoId }));
    }

    const created = needsOcrFirst
      ? yield* call(
          serviceCreateDocumentCognitionJob,
          tdoId,
          {
            ocrEngineId: documentOcrEngineId,
            piiEngineId: documentPIIDetectionEngineId,
          },
          true,
          defaultClusterId ?? '',
          token,
          apiRoot,
          graphQLEndpoint,
          veritoneAppId
        )
      : yield* call(
          serviceCreatePiiDetectionJob,
          tdoId,
          pdfUrl,
          documentPIIDetectionEngineId,
          defaultClusterId ?? '',
          token,
          apiRoot,
          graphQLEndpoint,
          veritoneAppId
        );

    // Scoped to the document it was started on: the poller watching a DIFFERENT
    // document must ignore it. The task ids ride along so the poller watches
    // the new run by id rather than hunting the TDO's task list for it — both
    // of them, since a chained job starts OCR as well.
    yield* put(
      RUN_PII_DETECTION_SUCCESS({
        tdoId,
        jobId: created.jobId,
        piiTaskId: created.taskIdByEngineId[documentPIIDetectionEngineId],
        ...(documentOcrEngineId
          ? { ocrTaskId: created.taskIdByEngineId[documentOcrEngineId] }
          : {}),
        withOcr: needsOcrFirst,
      })
    );
    yield* put(
      enqueueSnackbar({
        message: needsOcrFirst
          ? 'Running OCR, then PII detection.'
          : 'PII detection started.',
        variant: 'info',
        // Explicit: this is an acknowledgement, not a state the reviewer has to
        // dismiss. Without it the notice sat on screen for the whole run.
        autoHideDuration: 5000,
      })
    );

    // RUN_PII_DETECTION_SUCCESS is what hands this job to the shared
    // engine-status poller, which is already watching this document and picks
    // the re-run up on its next tick. Nothing is polled from here.
  } catch (e: unknown) {
    const message =
      e instanceof Error ? e.message : 'Failed to start PII detection';
    if (failedFor) {
      yield* put(RUN_PII_DETECTION_FAILURE({ tdoId: failedFor, message }));
      if (startedOcr) {
        // The optimistic OCR announcement was for a job that never started.
        yield* put(OCR_NOT_RUN({ tdoId: failedFor }));
      }
    }
    yield* put(
      enqueueSnackbar({
        message: `Could not start PII detection: ${message}`,
        variant: 'error',
      })
    );
  }
}

export function* runPiiDetectionSaga() {
  // takeLatest so a re-click supersedes an in-flight submission. Watching the
  // resulting job is the shared engine-status poller's job, not this saga's.
  yield* takeLatest(RUN_PII_DETECTION, handleRunPiiDetection);
}
